import { apps } from '../data/apps';

const OAUTH_SERVER_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin 
  : 'http://localhost:8000';

// Define utility functions locally to avoid dependency on global window functions
const togglePassword = (passwordId: string, password: string, button: HTMLElement) => {
  const elem = document.getElementById(passwordId);
  if (elem) {
    if (elem.textContent === '••••••••') {
      elem.textContent = password;
      button.innerHTML = '🙈 Hide';
    } else {
      elem.textContent = '••••••••';
      button.innerHTML = '👁️ Show';
    }
  }
};

const copyToClipboard = (text: string, button: HTMLElement) => {
  navigator.clipboard.writeText(text).then(() => {
    const feedback = button.nextElementSibling as HTMLElement;
    if (feedback) {
      feedback.style.opacity = '1';
      setTimeout(() => {
        feedback.style.opacity = '0';
      }, 2000);
    }
  }).catch(() => {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    
    const feedback = button.nextElementSibling as HTMLElement;
    if (feedback) {
      feedback.style.opacity = '1';
      setTimeout(() => {
        feedback.style.opacity = '0';
      }, 2000);
    }
  });
};

export const performAutoLogin = (appId: string, userEmail: string, variantId?: string) => {
  const app = apps.find(a => a.id === appId);
  
  if (!app) {
    console.error('App not found:', appId);
    return;
  }

  if (!app.requiresLogin) {
    // For non-login required apps, use variant URL if available
    const url = variantId && app.variants ? 
      app.variants.find(v => v.id === variantId)?.url || app.url : 
      app.url;
    window.open(url, '_blank');
    return;
  }

  if (app.oauthConfig) {
    // Store return URL to come back to SSO dashboard after OAuth
    sessionStorage.setItem('sso_return_url', window.location.href);
    sessionStorage.setItem('sso_user_email', userEmail);
    sessionStorage.setItem('sso_target_app', appId);
    if (variantId) {
      sessionStorage.setItem('sso_target_variant', variantId);
    }
    
    // Use OAuth flow
    initiateOAuthFlow(app.oauthConfig);
  } else {
    // Fallback to credential display (for apps not yet OAuth-enabled)
    let credentials;
    const userCreds = app.credentials?.[userEmail];
    
    if (variantId && userCreds && typeof userCreds === 'object' && !userCreds.username) {
      // Multi-variant credentials structure
      credentials = (userCreds as any)[variantId];
    } else {
      // Single credential structure
      credentials = userCreds;
    }
    
    if (credentials) {
      const variant = variantId && app.variants ? app.variants.find(v => v.id === variantId) : null;
      const appUrl = variant?.url || app.url;
      showCredentialsModal(app, credentials, variant, appUrl);
    } else {
      const url = variantId && app.variants ? 
        app.variants.find(v => v.id === variantId)?.url || app.url : 
        app.url;
      window.open(url, '_blank');
    }
  }
};

const initiateOAuthFlow = (oauthConfig: any) => {
  // Always use the standard OAuth flow, but mark that user is coming from SSO hub
  // This allows the OAuth login page to auto-authenticate if user is already logged in
  
  const currentUser = localStorage.getItem('current_user');
  
  // Generate a random state parameter for security
  const state = generateRandomString(32);
  
  // Store state in sessionStorage for validation
  sessionStorage.setItem('oauth_state', state);
  
  // Store the fact that user is coming from SSO hub (so login page can auto-submit)
  if (currentUser) {
    sessionStorage.setItem('sso_auto_login', currentUser);
  }
  
  // Build OAuth authorization URL
  const authParams = new URLSearchParams({
    client_id: oauthConfig.clientId,
    redirect_uri: oauthConfig.redirectUri,
    response_type: 'code',
    scope: oauthConfig.scope,
    state: state
  });
  
  const authUrl = `${OAUTH_SERVER_URL}/api/oauth/authorize?${authParams}`;
  
  // Redirect to OAuth authorization flow in same tab for seamless SSO
  window.location.href = authUrl;
};


const generateRandomString = (length: number) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Global functions are now defined in index.tsx

// Fallback function for non-OAuth apps
const showCredentialsModal = (app: any, credentials: any, variant?: any, appUrl?: string) => {
  const modalId = 'credentials-modal-' + Date.now();
  const passwordId = 'password-' + modalId;
  
  // Create modal using DOM API to avoid any template literal issues
  const modal = document.createElement('div');
  modal.id = modalId;
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(5px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: white;
    padding: 2rem;
    border-radius: 12px;
    max-width: 450px;
    width: 90%;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  `;

  // Create header
  const header = document.createElement('h3');
  header.style.cssText = 'margin-top: 0; color: #333; font-size: 1.5rem;';
  const appTitle = variant ? `${app.name} (${variant.name})` : app.name;
  header.innerHTML = `<span style="color: #007bff;">🔐</span> Login to ${appTitle}`;
  
  const description = document.createElement('p');
  description.style.cssText = 'color: #666; margin-bottom: 1.5rem; line-height: 1.5;';
  const descText = variant ? 
    `Access ${variant.description.toLowerCase()}. OAuth integration is coming soon! For now, use these credentials:` :
    'OAuth integration is coming soon! For now, use these credentials to access the application:';
  description.textContent = descText;
  
  // Create credentials container
  const credentialsDiv = document.createElement('div');
  credentialsDiv.style.cssText = 'background: #f8f9fa; padding: 1.5rem; border-radius: 8px; margin-bottom: 1.5rem; border: 1px solid #e9ecef;';
  
  // Username section
  const usernameDiv = document.createElement('div');
  usernameDiv.style.cssText = 'margin-bottom: 1rem; position: relative;';
  
  const usernameLabel = document.createElement('strong');
  usernameLabel.style.cssText = 'display: inline-block; width: 80px; color: #495057;';
  usernameLabel.textContent = 'Username:';
  
  const usernameSpan = document.createElement('span');
  usernameSpan.style.cssText = 'font-family: "Courier New", monospace; background: white; padding: 6px 10px; border-radius: 4px; border: 1px solid #dee2e6; display: inline-block; min-width: 200px; margin-left: 0.5rem;';
  usernameSpan.textContent = credentials.username;
  
  const copyUsernameBtn = document.createElement('button');
  copyUsernameBtn.className = 'copy-username-btn';
  copyUsernameBtn.style.cssText = 'background: #28a745; color: white; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.85rem; margin-left: 0.5rem;';
  copyUsernameBtn.textContent = '📋 Copy';
  
  const usernameFeedback = document.createElement('span');
  usernameFeedback.style.cssText = 'margin-left: 0.5rem; color: #28a745; font-size: 0.85rem; opacity: 0; transition: opacity 0.3s;';
  usernameFeedback.textContent = 'Copied!';
  
  usernameDiv.appendChild(usernameLabel);
  usernameDiv.appendChild(usernameSpan);
  usernameDiv.appendChild(copyUsernameBtn);
  usernameDiv.appendChild(usernameFeedback);
  
  // Password section
  const passwordDiv = document.createElement('div');
  passwordDiv.style.cssText = 'margin-bottom: 1rem; position: relative;';
  
  const passwordLabel = document.createElement('strong');
  passwordLabel.style.cssText = 'display: inline-block; width: 80px; color: #495057;';
  passwordLabel.textContent = 'Password:';
  
  const passwordSpan = document.createElement('span');
  passwordSpan.id = passwordId;
  passwordSpan.style.cssText = 'font-family: "Courier New", monospace; background: white; padding: 6px 10px; border-radius: 4px; border: 1px solid #dee2e6; display: inline-block; min-width: 200px; margin-left: 0.5rem;';
  passwordSpan.textContent = '••••••••';
  
  const showPasswordBtn = document.createElement('button');
  showPasswordBtn.className = 'show-password-btn';
  showPasswordBtn.style.cssText = 'background: #007bff; color: white; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.85rem; margin-left: 0.5rem;';
  showPasswordBtn.textContent = '👁️ Show';
  
  const copyPasswordBtn = document.createElement('button');
  copyPasswordBtn.className = 'copy-password-btn';
  copyPasswordBtn.style.cssText = 'background: #28a745; color: white; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.85rem; margin-left: 0.25rem;';
  copyPasswordBtn.textContent = '📋 Copy';
  
  const passwordFeedback = document.createElement('span');
  passwordFeedback.style.cssText = 'margin-left: 0.5rem; color: #28a745; font-size: 0.85rem; opacity: 0; transition: opacity 0.3s;';
  passwordFeedback.textContent = 'Copied!';
  
  passwordDiv.appendChild(passwordLabel);
  passwordDiv.appendChild(passwordSpan);
  passwordDiv.appendChild(showPasswordBtn);
  passwordDiv.appendChild(copyPasswordBtn);
  passwordDiv.appendChild(passwordFeedback);
  
  credentialsDiv.appendChild(usernameDiv);
  credentialsDiv.appendChild(passwordDiv);
  
  // Company section if exists
  if (credentials.companyName) {
    const companyDiv = document.createElement('div');
    companyDiv.style.cssText = 'margin-bottom: 1rem; position: relative;';
    
    const companyLabel = document.createElement('strong');
    companyLabel.style.cssText = 'display: inline-block; width: 80px; color: #495057;';
    companyLabel.textContent = 'Company:';
    
    const companySpan = document.createElement('span');
    companySpan.style.cssText = 'font-family: "Courier New", monospace; background: white; padding: 6px 10px; border-radius: 4px; border: 1px solid #dee2e6; display: inline-block; min-width: 200px; margin-left: 0.5rem;';
    companySpan.textContent = credentials.companyName;
    
    const copyCompanyBtn = document.createElement('button');
    copyCompanyBtn.className = 'copy-company-btn';
    copyCompanyBtn.style.cssText = 'background: #28a745; color: white; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.85rem; margin-left: 0.5rem;';
    copyCompanyBtn.textContent = '📋 Copy';
    
    const companyFeedback = document.createElement('span');
    companyFeedback.style.cssText = 'margin-left: 0.5rem; color: #28a745; font-size: 0.85rem; opacity: 0; transition: opacity 0.3s;';
    companyFeedback.textContent = 'Copied!';
    
    companyDiv.appendChild(companyLabel);
    companyDiv.appendChild(companySpan);
    companyDiv.appendChild(copyCompanyBtn);
    companyDiv.appendChild(companyFeedback);
    credentialsDiv.appendChild(companyDiv);
  }
  
  // Role section if exists
  if (credentials.role) {
    const roleDiv = document.createElement('div');
    roleDiv.style.cssText = 'position: relative;';
    
    const roleLabel = document.createElement('strong');
    roleLabel.style.cssText = 'display: inline-block; width: 80px; color: #495057;';
    roleLabel.textContent = 'Role:';
    
    const roleSpan = document.createElement('span');
    roleSpan.style.cssText = 'font-family: "Courier New", monospace; background: white; padding: 6px 10px; border-radius: 4px; border: 1px solid #dee2e6; display: inline-block; min-width: 200px; margin-left: 0.5rem;';
    roleSpan.textContent = credentials.role;
    
    roleDiv.appendChild(roleLabel);
    roleDiv.appendChild(roleSpan);
    credentialsDiv.appendChild(roleDiv);
  }
  
  // Button container
  const buttonDiv = document.createElement('div');
  buttonDiv.style.cssText = 'display: flex; gap: 1rem; justify-content: center;';
  
  const openBtn = document.createElement('button');
  openBtn.id = 'openApp';
  openBtn.style.cssText = 'padding: 0.75rem 2rem; background: #007bff; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 1rem; font-weight: 500; transition: background 0.2s;';
  const btnText = variant ? `🚀 Open ${variant.name}` : `🚀 Open ${app.name}`;
  openBtn.textContent = btnText;
  
  const closeBtn = document.createElement('button');
  closeBtn.id = 'closeModal';
  closeBtn.style.cssText = 'padding: 0.75rem 1.5rem; background: #6c757d; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 1rem; transition: background 0.2s;';
  closeBtn.textContent = 'Cancel';
  
  buttonDiv.appendChild(openBtn);
  buttonDiv.appendChild(closeBtn);
  
  // Assemble modal
  modalContent.appendChild(header);
  modalContent.appendChild(description);
  modalContent.appendChild(credentialsDiv);
  modalContent.appendChild(buttonDiv);

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // Add event listeners - now we have direct references to the elements
  openBtn.addEventListener('click', () => {
    const targetUrl = appUrl || app.url;
    window.open(targetUrl, '_blank');
  });
  openBtn.addEventListener('mouseover', () => {
    openBtn.style.background = '#0056b3';
  });
  openBtn.addEventListener('mouseout', () => {
    openBtn.style.background = '#007bff';
  });

  closeBtn.addEventListener('click', () => {
    document.body.removeChild(modal);
  });
  closeBtn.addEventListener('mouseover', () => {
    closeBtn.style.background = '#5a6268';
  });
  closeBtn.addEventListener('mouseout', () => {
    closeBtn.style.background = '#6c757d';
  });

  // Copy username button
  copyUsernameBtn.addEventListener('click', () => {
    copyToClipboard(credentials.username, copyUsernameBtn);
  });

  // Show/hide password button
  showPasswordBtn.addEventListener('click', () => {
    togglePassword(passwordId, credentials.password, showPasswordBtn);
  });

  // Copy password button
  copyPasswordBtn.addEventListener('click', () => {
    copyToClipboard(credentials.password, copyPasswordBtn);
  });

  // Copy company button (if exists)
  if (credentials.companyName) {
    const copyCompanyBtn = modalContent.querySelector('.copy-company-btn') as HTMLElement;
    copyCompanyBtn?.addEventListener('click', () => {
      copyToClipboard(credentials.companyName, copyCompanyBtn);
    });
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });

};